if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (this.form) {
          const variantInput = this.form.querySelector('[name="id"]');
          if (variantInput) variantInput.disabled = false;
          this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        }
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton?.querySelector('span') || this.submitButton;

        if (this.submitButton && document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.handleFormChange = this.handleFormChange.bind(this);
        if (this.form) {
          this.form.addEventListener('change', this.handleFormChange);
        }
        setTimeout(() => {
          this.scheduleInventoryValidation(0);
        }, 0);
      }

      getFulfillmentGate() {
        return window.EtFulfillmentGate || null;
      }

      getQuantityInputElement() {
        if (!this.form) return null;
        const inForm = this.form.querySelector('input[name="quantity"]');
        if (inForm) return inForm;

        // Quantity input may live outside the <form> and be linked via form="<id>".
        const formId = `${this.form.getAttribute('id') || ''}`.trim();
        if (formId) {
          return document.querySelector(`input[name="quantity"][form="${formId}"]`);
        }
        return null;
      }

      getRequestedQuantity() {
        const quantityInput = this.getQuantityInputElement();
        const rawValue = quantityInput ? `${quantityInput.value || ''}`.trim() : '';
        const parsed = parseInt(rawValue || '1', 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
      }

      resolveVariantForSelectedCatalog() {
        const variantInput = this.form ? this.form.querySelector('input[name="id"]') : null;
        if (!variantInput) {
          return { variantId: '', catalogId: '', selectedCatalogId: '' };
        }

        const fallbackVariantId = `${variantInput.value || ''}`.trim();
        const isB2B = document.body && document.body.getAttribute('data-b2b-customer') === 'true';
        if (isB2B) {
          return {
            variantId: fallbackVariantId,
            catalogId: `${variantInput.getAttribute('data-variant-catalog-id') || ''}`.trim(),
            selectedCatalogId: ''
          };
        }

        const selectedCatalogId = `${localStorage.getItem('selected_catalog_id') || ''}`.trim();
        if (!selectedCatalogId) {
          return {
            variantId: fallbackVariantId,
            catalogId: `${variantInput.getAttribute('data-variant-catalog-id') || ''}`.trim(),
            selectedCatalogId: ''
          };
        }

        let targetVariantId = fallbackVariantId;
        let targetCatalogId = `${variantInput.getAttribute('data-variant-catalog-id') || ''}`.trim();
        const productNode =
          (this.form && this.form.closest('[data-product-id]')) ||
          this.closest('[data-product-id]');
        const productId = productNode ? String(productNode.getAttribute('data-product-id') || '').trim() : '';
        const productContainer =
          this.closest('[data-product-id]') ||
          this.closest('.product-card') ||
          this.closest('.card-wrapper') ||
          document;
        const variantElements = productContainer.querySelectorAll('[data-variant-catalog-id][data-variant-id]');

        variantElements.forEach((element) => {
          const catalogId = `${element.getAttribute('data-variant-catalog-id') || ''}`.trim();
          const variantId = `${element.getAttribute('data-variant-id') || ''}`.trim();
          if (!catalogId || !variantId || catalogId !== selectedCatalogId) return;
          targetVariantId = variantId;
          targetCatalogId = catalogId;
        });

        if ((!targetVariantId || targetCatalogId !== selectedCatalogId) && window.variantMapping) {
          let mappingEntry = window.variantMapping[selectedCatalogId];
          if (mappingEntry) {
            const mappingProductId = String(
              mappingEntry.productId || mappingEntry.product_id || ''
            ).trim();
            if (productId && mappingProductId && mappingProductId !== productId) {
              mappingEntry = null;
            }
          }

          if (mappingEntry) {
            targetVariantId = `${mappingEntry.id || ''}`.trim();
            targetCatalogId = selectedCatalogId;
          }
        }

        if (targetVariantId) {
          variantInput.value = targetVariantId;
        }

        return {
          variantId: targetVariantId || fallbackVariantId,
          catalogId: targetCatalogId,
          selectedCatalogId: selectedCatalogId
        };
      }

      handleFulfillmentValidationFailure(variantId) {
        if (typeof openDeliveryModal === 'function') {
          openDeliveryModal(variantId);
        }
        this.clearAddToCartLoading(true);
        if (window.cartOperationInProgress) {
          delete window.cartOperationInProgress[variantId];
        }
        if (window.inventoryValidator) {
          window.inventoryValidator.clearPendingAdd(variantId);
        }
        return false;
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        // Resolve variant from selected catalog before validation.
        const resolvedVariant = this.resolveVariantForSelectedCatalog();
        const variantId = resolvedVariant.variantId;
        
        // Check if there's already a cart operation in progress for this variant
        if (window.cartOperationInProgress && window.cartOperationInProgress[variantId]) {
          return;
        }

        // Mark cart operation as in progress
        if (!window.cartOperationInProgress) {
          window.cartOperationInProgress = {};
        }
        window.cartOperationInProgress[variantId] = true;

        this.submitButton.classList.add('loading');
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.handleErrorMessage();

        // Check if this is a gift card product
        const isGiftCard = this.submitButton.getAttribute('data-is-gift-card') === 'true';
        if (isGiftCard) {
          // Gift cards don't need fulfillment validation
          this.proceedWithAddToCart();
          return;
        }

        // Get quantity to add for inventory check
        const quantityToAdd = this.getRequestedQuantity();
        const productNode = (this.form && this.form.closest('[data-product-id]')) || this.closest('[data-product-id]');
        const productId = productNode ? String(productNode.getAttribute('data-product-id') || '') : '';

        // Check inventory before proceeding
        if (window.inventoryValidator) {
          window.inventoryValidator.canAddToCart(variantId, quantityToAdd, { productId: productId || null })
            .then(async (checkResult) => {
              if (!checkResult.canAdd) {
                this.clearAddToCartLoading(true);
                
                // Clear the cart operation lock since we're not proceeding
                if (window.cartOperationInProgress) {
                  delete window.cartOperationInProgress[variantId];
                }

                if (this.submitButton && typeof window.inventoryValidator.updateButtonState === 'function') {
                  await window.inventoryValidator.updateButtonState(this.submitButton, variantId, false, { productId: productId || null });
                }

                // Show inventory warning
                if (checkResult.reason === 'exceeds_inventory' || checkResult.reason === 'no_inventory_info') {
                  window.inventoryValidator.showInventoryWarning(variantId, checkResult);
                } else if (checkResult.reason === 'inventory_prefetch_pending') {
                  if (typeof window.inventoryValidator.showInventoryLoadingWarning === 'function') {
                    window.inventoryValidator.showInventoryLoadingWarning();
                  }
                }
                return;
              }
              // Inventory check passed - track pending before proceeding (prevents double-add on rapid clicks)
              window.inventoryValidator.trackPendingAdd(variantId, quantityToAdd);
              await this.checkCartAttributesAndProceed(variantId);
            })
            .catch(async (error) => {
              console.error('ProductForm: Error checking inventory:', error);
              this.clearAddToCartLoading(true);
              
              // Clear the cart operation lock on error
              if (window.cartOperationInProgress) {
                delete window.cartOperationInProgress[variantId];
              }

              // Clear pending add tracking on error
              window.inventoryValidator.clearPendingAdd(variantId);
              if (typeof window.inventoryValidator.showInventoryLoadingWarning === 'function') {
                window.inventoryValidator.showInventoryLoadingWarning();
              }
              this.scheduleInventoryValidation(120);
            });
        } else {
          await this.checkCartAttributesAndProceed(variantId);
        }
      }

      async checkCartAttributesAndProceed(variantId) {
        const gate = this.getFulfillmentGate();
        if (!gate || typeof gate.validateForAction !== 'function') {
          console.error('ProductForm: EtFulfillmentGate is unavailable');
          return this.handleFulfillmentValidationFailure(variantId);
        }

        let validation;
        try {
          validation = await gate.validateForAction();
        } catch (error) {
          console.error('ProductForm: Fulfillment validation failed unexpectedly:', error);
          return this.handleFulfillmentValidationFailure(variantId);
        }

        if (!validation || validation.ok !== true) {
          return this.handleFulfillmentValidationFailure(variantId);
        }

        this.proceedWithAddToCart();
        return true;
      }

      proceedWithAddToCart() {
        this.submitButton.setAttribute('aria-disabled', true);
     

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
              
              // Store the variant ID and timestamp for inventory re-check
              this.lastAddedVariant = formData.get('id');
              this.lastAddedTime = Date.now();

              const addedVariantId = formData.get('id');

              if (window.inventoryValidator && typeof window.inventoryValidator.schedulePageValidation === 'function' && addedVariantId) {
                window.inventoryValidator._cartSnapshot = null;
                window.inventoryValidator._cartSnapshotAt = 0;

                if (window.cartWatcher && typeof window.cartWatcher.emitCartChanges === 'function') {
                  Promise.resolve(window.cartWatcher.emitCartChanges()).catch(() => {});
                }

                setTimeout(() => {
                  window.inventoryValidator.schedulePageValidation(0, [String(addedVariantId)]);
                }, 320);

                setTimeout(() => {
                  window.inventoryValidator.schedulePageValidation(0, [String(addedVariantId)]);
                }, 700);
              }

              if (window.inventoryChecker && typeof window.inventoryChecker.updateProductCards === 'function') {
                setTimeout(() => {
                  window.inventoryChecker.updateProductCards(null, { forceFreshCart: true })
                    .catch(() => {});
                  if (window.inventoryValidator && typeof window.inventoryValidator.schedulePageValidation === 'function' && addedVariantId) {
                    window.inventoryValidator.schedulePageValidation(120, [String(addedVariantId)]);
                  }
                }, 220);
              }
            }
            this.error = false;

            // Update cart icon/count through normal section rendering, but don't auto-open drawer
            if (this.cart && typeof this.cart.renderContents === 'function') {
              this.cart.renderContents(response);
            }

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              quickAddModal.hide(true);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(async () => {
            this.clearAddToCartLoading(false);
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            
            const variantId = formData.get('id');
            
            if (!this.error) {
              // For bundles, verify inventory before re-enabling button
              const escapedId = (window.EtInventoryDomain && window.EtInventoryDomain.escapeForSelector)
                ? window.EtInventoryDomain.escapeForSelector(String(variantId))
                : String(variantId);
              const productContainer = this.closest('[data-product-id]') || this.closest('.product-card') || this.closest('.card-wrapper') || document;
              const card = productContainer.querySelector ? productContainer.querySelector(`[data-variant-id="${escapedId}"]`) : document.querySelector(`[data-variant-id="${escapedId}"]`);
              const isBundle = card?.getAttribute('data-is-bundle') === 'true';
              
              if (isBundle && window.inventoryChecker) {
                // Wait a moment for cart to be updated, then check inventory
                setTimeout(async () => {
                  const productId = card?.getAttribute('data-product-id');
                  
                  // Get bundle inventory (calculated from components)
                  let inventoryInfo = window.inventoryChecker.findVariantInventory(variantId);
                  if (!inventoryInfo && productId) {
                    inventoryInfo = window.inventoryChecker.getBundleInventory(productId);
                  }
                  
                  if (inventoryInfo) {
                    const cartQuantity = await window.inventoryChecker.getCartQuantityForVariant(variantId);
                    const shouldDisable = cartQuantity >= inventoryInfo.available;
                    
                    if (shouldDisable) {
                      this.submitButton.setAttribute('aria-disabled', true);
                      
                      // Update the card's sold out state
                      if (card) {
                        card.setAttribute('data-inventory-sold-out', true);
                        window.inventoryChecker.updateCardVisualState(card, true, inventoryInfo.available);
                      }
                    } else {
                      this.submitButton.removeAttribute('aria-disabled');
                    }
                  } else {
                    this.submitButton.removeAttribute('aria-disabled');
                  }
                  
                  // Clear the cart operation lock after inventory check
                  if (window.cartOperationInProgress) {
                    delete window.cartOperationInProgress[variantId];
                  }
                  
                  // Clear pending add tracking - operation complete
                  if (window.inventoryValidator) {
                    window.inventoryValidator.clearPendingAdd(variantId);
                  }
                }, 200);
              } else {
                this.submitButton.removeAttribute('aria-disabled');
                
                // Clear the cart operation lock
                if (window.cartOperationInProgress) {
                  delete window.cartOperationInProgress[variantId];
                }
                
                // Clear pending add tracking - operation complete
                if (window.inventoryValidator) {
                  window.inventoryValidator.clearPendingAdd(variantId);
                }
              }
            } else {
              // Clear the cart operation lock on error
              if (window.cartOperationInProgress) {
                delete window.cartOperationInProgress[variantId];
              }
              
              // Clear pending add tracking on error
              if (window.inventoryValidator) {
                window.inventoryValidator.clearPendingAdd(variantId);
              }
            }
          });
      }

      handleFormChange(event) {
        const target = event && event.target ? event.target : null;
        if (!target) return;
        const fieldName = target.getAttribute('name');
        if (fieldName === 'id' || fieldName === 'quantity') {
          this.scheduleInventoryValidation(60);
        }
      }

      scheduleInventoryValidation(delayMs = 80) {
        if (!this.submitButton || !this.form) return;
        if (this.submitButton.getAttribute('data-is-gift-card') === 'true') return;
        if (!window.inventoryValidator || typeof window.inventoryValidator.schedulePageValidation !== 'function') return;
        const variantInput = this.form.querySelector('input[name="id"]');
        const variantId = variantInput ? `${variantInput.value || ''}`.trim() : '';
        if (!variantId) return;
        window.inventoryValidator.schedulePageValidation(delayMs, [variantId]);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      clearAddToCartLoading(alsoReenable = false) {
        this.submitButton?.classList.remove('loading');
        if (alsoReenable) this.submitButton?.removeAttribute('aria-disabled');
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text && this.submitButtonText) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.submitButtonText) this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
